import { useEffect, useState, type ReactNode } from 'react';
import {
  DEFAULT_PROJECT_COLOR,
  SENIORITIES,
  type DealProbability,
  type DealReason,
  type DealReasonKind,
  type DealStatus,
  type PlaceholderProfile,
  type ProjectRole,
  type Seniority,
} from '@forecastify/shared';
import { Button, Dialog, Select, Stepper, Switch, Textarea, TextInput } from '@forecastify/ui';
import { useT } from '../../i18n';
import { Field } from '../card';
import { ColorPicker } from '../delivery/color-picker';
import { seniorityLabel } from './deal-kit';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 20 Oct 2026 · Phase 6 · D1 · the Deal menedzsment dialogs.                │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **A dialog per row, where the mockup edits inline.** `phase6/16` draws every status as an
 * always-open card of inputs that write on each keystroke — a prototype's convenience with no
 * server behind it. Here every save is one versioned request, so it is a form with a Save button,
 * the same as every other settings vocabulary (`project-roles-section.tsx`).
 *
 * ⚠️ **`onSubmit` answers with the refusal or `null`.** A refused save keeps the dialog open with
 * the sentence under the form, so nothing typed is lost to a duplicate name.
 */

export type Submit<B> = (body: B) => Promise<string | null>;

/** The footer and error line every dialog here shares. */
function Footer({
  error,
  busy,
  onCancel,
  onSave,
}: {
  readonly error: string | null;
  readonly busy: boolean;
  readonly onCancel: () => void;
  readonly onSave: () => void;
}) {
  const t = useT();
  return (
    <>
      {error === null ? null : (
        <div className="text-destructive text-[12px]" role="alert">
          {error}
        </div>
      )}
      <div className="mt-1 flex items-center justify-end gap-2">
        <Button variant="outline" size="lg" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button size="lg" disabled={busy} onClick={onSave}>
          {t('common.save')}
        </Button>
      </div>
    </>
  );
}

/** Runs a save: busy while it is out, closes on success, keeps the refusal otherwise. */
function useSave(onClose: () => void) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const run = (send: () => Promise<string | null>): void => {
    setBusy(true);
    void send().then((refusal) => {
      setBusy(false);
      if (refusal === null) onClose();
      else setError(refusal);
    });
  };
  return { error, setError, busy, run };
}

/** `''`, `'12'` → an integer in range, or null. */
function intIn(value: string, min: number, max: number): number | null {
  if (!/^\d+$/u.test(value.trim())) return null;
  const n = Number(value.trim());
  return n >= min && n <= max ? n : null;
}

/** `9 000`, `9000,5` → a non-negative amount, or null. */
export function amountOf(value: string): number | null {
  const n = Number(value.replace(/\s/gu, '').replace(',', '.'));
  return value.trim() === '' || !Number.isFinite(n) || n < 0 ? null : n;
}

/** A switch with its label and note beside it — the mockup's *Fegyelem és cél* rows. */
export function SwitchRow({
  label,
  note,
  checked,
  disabled = false,
  onChange,
}: {
  readonly label: string;
  readonly note?: string;
  readonly checked: boolean;
  readonly disabled?: boolean;
  readonly onChange: (next: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        label={label}
        className="mt-0.5"
      />
      <div className="min-w-0">
        <div className="text-[12.5px] font-medium">{label}</div>
        {note === undefined ? null : (
          <div className="text-muted-foreground text-[11.5px] leading-snug">{note}</div>
        )}
      </div>
    </div>
  );
}

/* ── a status ─────────────────────────────────────────────────────────────────────────────── */

export interface StatusBody {
  name: string;
  color: string;
  probability: number;
  rotDays: number;
  criteria: string;
  version?: number;
}

export function StatusDialog({
  editing,
  onClose,
  onSubmit,
}: {
  readonly editing: DealStatus | 'new' | null;
  readonly onClose: () => void;
  readonly onSubmit: Submit<StatusBody>;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_PROJECT_COLOR);
  const [probability, setProbability] = useState('50');
  const [rot, setRot] = useState('14');
  const [criteria, setCriteria] = useState('');
  const save = useSave(onClose);
  const { setError } = save;

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    setName(editing === 'new' ? '' : editing.name);
    setColor(editing === 'new' ? DEFAULT_PROJECT_COLOR : editing.color);
    setProbability(editing === 'new' ? '50' : String(editing.probability));
    setRot(editing === 'new' ? '14' : String(editing.rotDays));
    setCriteria(editing === 'new' ? '' : editing.criteria);
  }, [editing, setError]);

  /* ⚠️ The won and lost pair keep their probability and rot — the server stores whatever it is
     sent, so the dialog sends back what it read rather than offering fields that mean nothing on
     a closed deal. */
  const closed = editing !== null && editing !== 'new' && editing.category !== 'open';

  const submit = (): void => {
    if (name.trim() === '') {
      setError(t('dealSettings.statuses.error.name'));
      return;
    }
    const pct = intIn(probability, 0, 100);
    if (pct === null) {
      setError(t('dealSettings.statuses.error.probability'));
      return;
    }
    const days = intIn(rot, 0, 365);
    if (days === null) {
      setError(t('dealSettings.statuses.error.rot'));
      return;
    }
    save.run(() =>
      onSubmit({
        name: name.trim(),
        color,
        probability: pct,
        rotDays: days,
        criteria: criteria.trim(),
        ...(editing !== null && editing !== 'new' ? { version: editing.version } : {}),
      }),
    );
  };

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(editing === 'new' ? 'dealSettings.statuses.new' : 'dealSettings.statuses.edit')}
      description={closed ? t('dealSettings.statuses.closedNote') : undefined}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealSettings.statuses.field.name')} required>
          <TextInput
            autoFocus
            value={name}
            placeholder={t('dealSettings.statuses.field.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        {closed ? null : (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label={t('dealSettings.statuses.field.probability')}
              note={t('dealSettings.statuses.field.probabilityNote')}
            >
              <Stepper
                value={probability}
                min={0}
                max={100}
                step={5}
                aria-label={t('dealSettings.statuses.field.probability')}
                onChange={(next) => {
                  setProbability(next);
                  setError(null);
                }}
              />
            </Field>
            <Field
              label={t('dealSettings.statuses.field.rot')}
              note={t('dealSettings.statuses.field.rotNote')}
            >
              <Stepper
                value={rot}
                min={0}
                max={365}
                aria-label={t('dealSettings.statuses.field.rot')}
                onChange={(next) => {
                  setRot(next);
                  setError(null);
                }}
              />
            </Field>
          </div>
        )}
        <Field label={t('dealSettings.statuses.field.criteria')}>
          <Textarea
            value={criteria}
            rows={2}
            maxLength={400}
            placeholder={t('dealSettings.statuses.field.criteriaPlaceholder')}
            onChange={(event) => {
              setCriteria(event.target.value);
            }}
          />
        </Field>
        <ColorPicker value={color} onChange={setColor} label={t('common.color')} />
        <Footer error={save.error} busy={save.busy} onCancel={onClose} onSave={submit} />
      </div>
    </Dialog>
  );
}

/* ── a probability step ───────────────────────────────────────────────────────────────────── */

export interface ProbabilityBody {
  pct: number;
  name: string;
  description: string;
  color: string;
  version?: number;
}

export function ProbabilityDialog({
  editing,
  onClose,
  onSubmit,
}: {
  readonly editing: DealProbability | 'new' | null;
  readonly onClose: () => void;
  readonly onSubmit: Submit<ProbabilityBody>;
}) {
  const t = useT();
  const [pct, setPct] = useState('50');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>(DEFAULT_PROJECT_COLOR);
  const save = useSave(onClose);
  const { setError } = save;

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    setPct(editing === 'new' ? '50' : String(editing.pct));
    setName(editing === 'new' ? '' : editing.name);
    setDescription(editing === 'new' ? '' : editing.description);
    setColor(editing === 'new' ? DEFAULT_PROJECT_COLOR : editing.color);
  }, [editing, setError]);

  const submit = (): void => {
    const value = intIn(pct, 0, 100);
    if (value === null) {
      setError(t('dealSettings.probabilities.error.pct'));
      return;
    }
    if (name.trim() === '') {
      setError(t('dealSettings.probabilities.error.name'));
      return;
    }
    save.run(() =>
      onSubmit({
        pct: value,
        name: name.trim(),
        description: description.trim(),
        color,
        ...(editing !== null && editing !== 'new' ? { version: editing.version } : {}),
      }),
    );
  };

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(
        editing === 'new' ? 'dealSettings.probabilities.new' : 'dealSettings.probabilities.edit',
      )}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-[140px_minmax(0,1fr)]">
          <Field label={t('dealSettings.probabilities.field.pct')} required>
            <Stepper
              value={pct}
              min={0}
              max={100}
              step={5}
              aria-label={t('dealSettings.probabilities.field.pct')}
              onChange={(next) => {
                setPct(next);
                setError(null);
              }}
            />
          </Field>
          <Field label={t('dealSettings.probabilities.field.name')} required>
            <TextInput
              autoFocus
              value={name}
              placeholder={t('dealSettings.probabilities.field.namePlaceholder')}
              onChange={(event) => {
                setName(event.target.value);
                setError(null);
              }}
            />
          </Field>
        </div>
        <Field label={t('dealSettings.probabilities.field.description')}>
          <TextInput
            value={description}
            maxLength={200}
            placeholder={t('dealSettings.probabilities.field.descriptionPlaceholder')}
            onChange={(event) => {
              setDescription(event.target.value);
            }}
          />
        </Field>
        <ColorPicker value={color} onChange={setColor} label={t('common.color')} />
        <Footer error={save.error} busy={save.busy} onCancel={onClose} onSave={submit} />
      </div>
    </Dialog>
  );
}

/* ── a reason ─────────────────────────────────────────────────────────────────────────────── */

export interface ReasonBody {
  kind: DealReasonKind;
  name: string;
  groupName: string;
  active: boolean;
  needsNote: boolean;
  version?: number;
}

export type ReasonEditing = DealReason | { readonly new: DealReasonKind } | null;

export function ReasonDialog({
  editing,
  groups,
  onClose,
  onSubmit,
}: {
  readonly editing: ReasonEditing;
  /** The lost reasons' groups already in use — offered as suggestions. */
  readonly groups: readonly string[];
  readonly onClose: () => void;
  readonly onSubmit: Submit<ReasonBody>;
}) {
  const t = useT();
  const [name, setName] = useState('');
  const [group, setGroup] = useState('');
  const [active, setActive] = useState(true);
  const [needsNote, setNeedsNote] = useState(false);
  const [typingGroup, setTypingGroup] = useState(false);
  const save = useSave(onClose);
  const { setError } = save;

  const existing = editing !== null && !('new' in editing) ? editing : null;
  const kind: DealReasonKind =
    editing === null ? 'lost' : 'new' in editing ? editing.new : editing.kind;

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    const row = 'new' in editing ? null : editing;
    setName(row?.name ?? '');
    setGroup(row?.groupName ?? '');
    setActive(row?.active ?? true);
    setNeedsNote(row?.needsNote ?? false);
    setTypingGroup(false);
  }, [editing, setError]);

  const submit = (): void => {
    if (name.trim() === '') {
      setError(t('dealSettings.reasons.error.name'));
      return;
    }
    save.run(() =>
      onSubmit({
        kind,
        name: name.trim(),
        groupName: kind === 'won' ? '' : group.trim(),
        active,
        needsNote,
        ...(existing === null ? {} : { version: existing.version }),
      }),
    );
  };

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={
        existing !== null
          ? t('dealSettings.reasons.edit')
          : kind === 'won'
            ? t('dealSettings.reasons.newWon')
            : t('dealSettings.reasons.newLost')
      }
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field label={t('dealSettings.reasons.field.name')} required>
          <TextInput
            autoFocus
            value={name}
            maxLength={80}
            placeholder={t('dealSettings.reasons.field.namePlaceholder')}
            onChange={(event) => {
              setName(event.target.value);
              setError(null);
            }}
          />
        </Field>
        {kind === 'won' ? null : (
          <Field label={t('dealSettings.reasons.field.group')}>
            {/* ⚠️ The groups are whatever the reasons carry (`group_name`) — there is no group
                table — so the picker offers those in use and a new one is made by typing it. */}
            {typingGroup ? (
              <TextInput
                autoFocus
                value={group}
                maxLength={60}
                placeholder={t('dealSettings.reasons.field.groupPlaceholder')}
                onChange={(event) => {
                  setGroup(event.target.value);
                }}
              />
            ) : (
              <Select
                value={group}
                onChange={(event) => {
                  setGroup(event.target.value);
                }}
                options={[
                  { value: '', label: t('dealSettings.reasons.noGroup') },
                  ...groups.map((entry) => ({ value: entry, label: entry })),
                ]}
                action={{
                  label: t('dealSettings.reasons.field.newGroup'),
                  onSelect: () => {
                    setGroup('');
                    setTypingGroup(true);
                  },
                }}
              />
            )}
          </Field>
        )}
        {kind === 'won' ? null : (
          <SwitchRow
            label={t('dealSettings.reasons.field.needsNote')}
            note={t('dealSettings.reasons.field.needsNoteNote')}
            checked={needsNote}
            onChange={setNeedsNote}
          />
        )}
        <SwitchRow
          label={t('dealSettings.reasons.field.active')}
          note={t('dealSettings.reasons.field.activeNote')}
          checked={active}
          onChange={setActive}
        />
        <Footer error={save.error} busy={save.busy} onCancel={onClose} onSave={submit} />
      </div>
    </Dialog>
  );
}

/* ── a placeholder profile ────────────────────────────────────────────────────────────────── */

export interface ProfileBody {
  projectRoleId: string;
  seniority: Seniority;
  costRate: number;
  targetRate: number;
  version?: number;
}

export function ProfileDialog({
  editing,
  roles,
  currencyCode,
  onClose,
  onSubmit,
}: {
  readonly editing: PlaceholderProfile | 'new' | null;
  readonly roles: readonly ProjectRole[];
  readonly currencyCode: string;
  readonly onClose: () => void;
  readonly onSubmit: Submit<ProfileBody>;
}) {
  const t = useT();
  const [roleId, setRoleId] = useState('');
  const [seniority, setSeniority] = useState<Seniority>('medior');
  const [cost, setCost] = useState('');
  const [target, setTarget] = useState('');
  const save = useSave(onClose);
  const { setError } = save;

  useEffect(() => {
    if (editing === null) return;
    setError(null);
    setRoleId(editing === 'new' ? '' : editing.projectRoleId);
    setSeniority(editing === 'new' ? 'medior' : editing.seniority);
    setCost(editing === 'new' ? '' : String(Number(editing.costRate)));
    setTarget(editing === 'new' ? '' : String(Number(editing.targetRate)));
  }, [editing, setError]);

  const submit = (): void => {
    if (roleId === '') {
      setError(t('dealSettings.profiles.error.role'));
      return;
    }
    const costRate = amountOf(cost);
    const targetRate = amountOf(target);
    if (costRate === null || targetRate === null) {
      setError(t('dealSettings.profiles.error.rate'));
      return;
    }
    save.run(() =>
      onSubmit({
        projectRoleId: roleId,
        seniority,
        costRate,
        targetRate,
        ...(editing !== null && editing !== 'new' ? { version: editing.version } : {}),
      }),
    );
  };

  return (
    <Dialog
      open={editing !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t(editing === 'new' ? 'dealSettings.profiles.new' : 'dealSettings.profiles.edit')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('dealSettings.profiles.field.role')}
            required
            {...(roles.length === 0 ? { note: t('dealSettings.profiles.field.noRoles') } : {})}
          >
            <Select
              searchable
              value={roleId}
              onChange={(event) => {
                setRoleId(event.target.value);
                setError(null);
              }}
              options={[
                { value: '', label: t('dealSettings.profiles.field.pickRole') },
                ...roles.map((role) => ({ value: role.id, label: role.name })),
              ]}
            />
          </Field>
          <Field label={t('dealSettings.profiles.field.seniority')} required>
            <Select
              value={seniority}
              onChange={(event) => {
                setSeniority(event.target.value as Seniority);
              }}
              options={SENIORITIES.map((entry) => ({
                value: entry,
                label: seniorityLabel(t, entry),
              }))}
            />
          </Field>
          <Field label={t('dealSettings.profiles.field.cost', { currency: currencyCode })} required>
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={cost}
              placeholder="0"
              onChange={(event) => {
                setCost(event.target.value);
                setError(null);
              }}
            />
          </Field>
          <Field
            label={t('dealSettings.profiles.field.target', { currency: currencyCode })}
            required
          >
            <TextInput
              inputMode="decimal"
              className="tabular-nums"
              value={target}
              placeholder="0"
              onChange={(event) => {
                setTarget(event.target.value);
                setError(null);
              }}
            />
          </Field>
        </div>
        <Footer error={save.error} busy={save.busy} onCancel={onClose} onSave={submit} />
      </div>
    </Dialog>
  );
}

/* ── the delete confirmation ──────────────────────────────────────────────────────────────── */

export interface Deleting {
  readonly title: string;
  readonly body: ReactNode;
  readonly confirm: () => Promise<string | null>;
}

export function ConfirmDelete({
  deleting,
  onClose,
}: {
  readonly deleting: Deleting | null;
  readonly onClose: () => void;
}) {
  const t = useT();
  const save = useSave(onClose);
  const { setError } = save;

  useEffect(() => {
    setError(null);
  }, [deleting, setError]);

  return (
    <Dialog
      open={deleting !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={deleting?.title ?? ''}
      description={deleting?.body}
      closeLabel={t('common.close')}
    >
      {save.error === null ? null : (
        <div className="text-destructive mb-2 text-[12px]" role="alert">
          {save.error}
        </div>
      )}
      <div className="mt-1 flex items-center justify-end gap-2">
        <Button variant="outline" size="lg" onClick={onClose}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="destructive"
          size="lg"
          disabled={save.busy}
          onClick={() => {
            if (deleting !== null) save.run(deleting.confirm);
          }}
        >
          {t('common.delete')}
        </Button>
      </div>
    </Dialog>
  );
}
